import { WebsocketMessage, WS_TOKEN_PARAM } from "@dto/websocketMessage.dto";
import { useEffect, useState } from "react";
import { Log } from "src/service/log.service";
import { GLOB } from "src/util/Glob";

interface WsClient {
  count: number;
  ws: WebSocket;
  callbacks: Set<(data) => void>;
  failedReconnectAttempts: number;
  reconnectTimer?: ReturnType<typeof setTimeout>;
}

const wsByUrl: Record<string, WsClient> = {};

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 60_000;

function scheduleReconnect(url: string, wsc: WsClient) {
  if (wsByUrl[url] !== wsc || wsc.count <= 0 || wsc.reconnectTimer) return;
  const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** wsc.failedReconnectAttempts);
  wsc.failedReconnectAttempts++;
  wsc.reconnectTimer = setTimeout(() => {
    wsc.reconnectTimer = null;
    connect(url, wsc);
  }, delay);
}

function connect(url: string, wsc: WsClient) {
  GLOB.xormonReady.promise.then(() => {
    // entry was deregistered or replaced while waiting, do not create an orphaned socket
    if (wsByUrl[url] !== wsc || wsc.count <= 0) return;
    // another (re)connect is already in progress
    if (wsc.ws && wsc.ws.readyState <= WebSocket.OPEN) return;
    const ws = wsc.ws = new WebSocket(`${GLOB.SERVER_WS_URL}${url}?${WS_TOKEN_PARAM}=${GLOB.userInfo?.wsToken}`);
    ws.onopen = () => {
      if (wsc.failedReconnectAttempts)
        Log.info(`WebSocket ${url} reconnected`);
      wsc.failedReconnectAttempts = 0;
    };
    ws.onerror = (ev) => wsc.failedReconnectAttempts ? console.debug(`WebSocket ${url} error`, ev) : Log.error(`WebSocket ${url} error`, ev);
    ws.onmessage = (ev) => {
      const data = JSON.parse(ev.data as string);
      wsc.callbacks.forEach(c => c(data));
    };
    ws.onclose = (ev) => {
      if (!wsc.failedReconnectAttempts && ev?.code && ev.code !== 1000 && !GLOB.isUpgrading) {
        Log.error(<div>WebSocket {url} closed with reason:<br />{ev.reason}</div>);
      }
      scheduleReconnect(url, wsc);
    };
  }, console.debug);
}

const useWebSocketSingleton = <T extends WebsocketMessage = WebsocketMessage>(url: string) => {
  const [data, setData] = useState<T>();

  useEffect(() => {
    let wsc = wsByUrl[url];

    if (!wsc) {
      wsc = wsByUrl[url] = { count: 0, ws: null, callbacks: new Set(), failedReconnectAttempts: 0 };
      connect(url, wsc);
    } else if (wsc.ws && wsc.ws.readyState > WebSocket.OPEN && !wsc.reconnectTimer) {
      // socket died and no reconnect is pending, reconnect for all subscribers
      connect(url, wsc);
    }
    wsc.count++;
    wsc.callbacks.add(setData);

    return () => {
      wsc.count--;
      wsc.callbacks.delete(setData);
      if (wsc.count <= 0) {
        clearTimeout(wsc.reconnectTimer);
        wsc.ws?.close();
        if (wsByUrl[url] === wsc) delete wsByUrl[url];
      }
    };
  }, []);

  return data;
};

export default useWebSocketSingleton;
